// Translations for SLÁI Events website

export const translations = {
  en: {
    // Header
    nav: {
      aboutUs: 'About Us',
      gallery: 'Gallery',
      reservations: 'Reservations',
      contact: 'Contact'
    },

    // Company Info
    company: {
      name: 'SLÁI Events',
      tagline: 'Premium Food Truck Catering',
      description: 'From our mobile food truck to your special occasion, we bring the best craft beers and gourmet sausages right to you. Whether it\'s a garden party, corporate event, or cocktail celebration, SLÁI Events delivers exceptional taste and memorable experiences.',
      email: 'info@slaievents.com',
      website: 'slaievents.com',
      addressLine1: 'Bärensteinstraße 2',
      addressLine2: '12685-Berlin',
      hours: 'Mon–Sun: By appointment'
    },

    // About Us Page
    aboutUs: {
      stats: {
        clientsLabel: 'Happy Clients',
        eventsLabel: 'Events Catered',
        ratingLabel: 'Customer Rating'
      },
      bookNow: 'Book Now',
      servicesTitle: 'Our Services',
      servicesSubtitle: 'From street food to sophisticated events, we\'ve got you covered',
      services: [
        {
          title: 'Food Truck Service',
          description: 'Our mobile food truck brings the party to you! Perfect for street events, festivals, and casual gatherings.',
          features: ['Premium craft beers', 'Gourmet sausages', 'Quick service', 'Perfect for events']
        },
        {
          title: 'Garden Parties',
          description: 'Transform your backyard into a culinary paradise. We cater intimate garden parties with style and flavor.',
          features: ['Custom menu planning', 'Full setup & cleanup', 'Beverage pairing', 'Outdoor grilling']
        },
        {
          title: 'Cocktail Events',
          description: 'Elevate your cocktail party with our signature cocktails and gourmet sausage selections.',
          features: ['Signature cocktails', 'Appetizer platters', 'Professional service', 'Elegant presentation']
        },
        {
          title: 'Birthday Celebrations',
          description: 'Make your special day unforgettable with our festive birthday catering packages for all ages.',
          features: ['Birthday packages', 'Themed decorations', 'Custom cake options', 'Entertainment coordination']
        },
        {
          title: 'Corporate Events',
          description: 'Impress your colleagues and clients with our professional corporate catering services and team-building experiences.',
          features: ['Professional setup', 'Customized menus', 'Team building activities', 'Full event management']
        },
        {
          title: 'Your Dream Event',
          description: 'Have a unique idea? We\'ll bring your vision to life with customized catering for any occasion.',
          features: ['Personalized menu creation', 'Flexible timing & setup', 'Custom themes available', 'Dedicated event planning']
        }
      ],
      storyTitle: 'Our Story',
      storyParagraphs: [
        'SLÁI Events started with a simple passion: bringing people together through great food and drinks. What began as a small food truck has grown into a full-service catering company specializing in unforgettable events.',
        'We pride ourselves on our selection of craft beers and gourmet sausages, carefully curated to provide the perfect pairing for any occasion. Whether it\'s a casual garden party or an elegant cocktail event, we bring the same level of dedication and quality to every gathering.',
        'Our team is passionate about creating memorable experiences. We don\'t just serve food and drinks—we create moments that bring people together and leave lasting impressions.'
      ]
    },

    // Gallery Page
    gallery: {
      title: 'Gallery',
      subtitle: 'Explore our delicious offerings and memorable events',
      categories: {
        all: 'All',
        foodTruck: 'Food Truck',
        food: 'Food',
        drinks: 'Drinks',
        events: 'Events'
      },
      ctaTitle: 'Want to be featured in our gallery?',
      ctaSubtitle: 'Book an event with us and create your own unforgettable memories!',
      ctaButton: 'Book Your Event',
      imageAlts: [
        'SLÁI Events food truck at outdoor event',
        'Gourmet sausages with craft beer',
        'Garden party setup',
        'Artisanal sausage selection',
        'Craft beer selection',
        'Cocktail party catering',
        'Food truck interior',
        'Happy customers at event',
        'Beer cocktails'
      ]
    },

    // Reservations Page
    reservations: {
      title: 'Reservations',
      subtitle: 'Choose your event type and let\'s create something memorable together',
      eventTypes: [
        {
          name: 'Food Truck Booking',
          description: 'Book our food truck for your event',
          minGuests: 20,
          duration: '2-4 hours'
        },
        {
          name: 'Garden Party Catering',
          description: 'Full catering service for garden parties',
          minGuests: 20,
          duration: '3-6 hours'
        },
        {
          name: 'Cocktail Party',
          description: 'Premium cocktail and appetizer service',
          minGuests: 15,
          duration: '2-5 hours'
        },
        {
          name: 'Birthday Party',
          description: 'Festive birthday celebration catering',
          minGuests: 20,
          duration: '3-5 hours'
        },
        {
          name: 'Corporate Event',
          description: 'Professional catering for business events',
          minGuests: 25,
          duration: '3-8 hours'
        },
        {
          name: 'Your Dream Event',
          description: 'What do you have in mind? Let\'s create it together!',
          minGuests: 20,
          duration: 'Flexible',
          isSpecial: true
        }
      ],
      minGuests: 'Min.',
      guests: 'guests',
      selectEvent: 'Select Event',
      howItWorksTitle: 'How It Works',
      steps: [
        {
          title: 'Choose Event Type',
          description: 'Select the perfect service for your occasion'
        },
        {
          title: 'Fill Details',
          description: 'Provide your event information and preferences'
        },
        {
          title: 'We\'ll Contact You',
          description: 'Our team will confirm and finalize the details'
        }
      ],
      formTitle: 'Book',
      formDescription: 'Fill in your details and we\'ll get back to you shortly to confirm your reservation.',
      formLabels: {
        fullName: 'Full Name',
        email: 'Email',
        phone: 'Phone Number',
        preferredDate: 'Preferred Date',
        preferredTime: 'Preferred Time',
        numberOfGuests: 'Number of Guests',
        specialRequests: 'Special Requests or Questions',
        minimum: 'Minimum'
      },
      formPlaceholders: {
        fullName: 'John Doe',
        email: 'john@example.com',
        phone: '+49 (123) 456-7890',
        specialRequests: 'Any dietary restrictions, special accommodations, or questions...'
      },
      formButtons: {
        cancel: 'Cancel',
        submit: 'Submit Request'
      },
      successTitle: 'Reservation Request Received!',
      successDescription: 'We\'ll contact you shortly to confirm your booking.'
    },

    // Contact Page
    contact: {
      title: 'Contact',
      subtitle: 'Have questions? We\'d love to hear from you. Send us a message and we\'ll respond as soon as possible.',
      infoCards: {
        phone: 'Phone',
        email: 'Email',
        location: 'Location',
        hours: 'Hours'
      },
      formTitle: 'Send Us a Message',
      formSubtitle: 'Fill out the form below and our team will get back to you within 24 hours',
      formLabels: {
        name: 'Your Name',
        email: 'Your Email',
        subject: 'Subject',
        message: 'Your Message'
      },
      formPlaceholders: {
        name: 'John Doe',
        email: 'john@example.com',
        subject: 'How can we help you?',
        message: 'Tell us more about your inquiry...'
      },
      sendButton: 'Send Message',
      successTitle: 'Message Sent!',
      successDescription: 'Thank you for reaching out. We\'ll get back to you soon.',
      mapTitle: 'Find Us',
      mapSubtitle: '',
      mapDescription: 'We\'re mobile — as a food truck service, we come to you, serving the Greater Metro Area.'
    },

    // Footer
    footer: {
      contactTitle: 'Contact',
      hoursTitle: 'Hours',
      availability: 'Available for events and special occasions.',
      bookAdvance: 'Book in advance!',
      copyright: 'All rights reserved.',
      privacyPolicy: 'Privacy Policy'
    },

    // Privacy Policy
    privacy: {
      title: 'Privacy Policy',
      lastUpdated: 'Last Updated',
      sections: {
        intro: {
          title: 'Introduction',
          content: 'SLÁI Events ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services.'
        },
        dataCollection: {
          title: 'Information We Collect',
          content: 'We collect information that you provide directly to us, including:',
          items: [
            'Name and contact information (email address, phone number)',
            'Event details and preferences',
            'Reservation information (date, time, number of guests)',
            'Communication records and feedback',
            'Any other information you choose to provide'
          ]
        },
        dataUsage: {
          title: 'How We Use Your Information',
          content: 'We use the information we collect for various purposes:',
          items: [
            'To process and manage your reservations and inquiries',
            'To communicate with you about our services',
            'To send you updates and promotional materials (with your consent)',
            'To improve our website and services',
            'To comply with legal obligations'
          ]
        },
        dataProtection: {
          title: 'Data Protection',
          content: 'We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet is 100% secure, and we cannot guarantee absolute security.'
        },
        cookies: {
          title: 'Cookies',
          content: 'Our website may use cookies and similar tracking technologies to enhance your browsing experience. You can set your browser to refuse all or some browser cookies, or to alert you when websites set or access cookies.'
        },
        thirdParties: {
          title: 'Third-Party Disclosure',
          content: 'We do not sell, trade, or otherwise transfer your personally identifiable information to outside parties unless we provide users with advance notice. This does not include website hosting partners and other parties who assist us in operating our website, conducting our business, or serving our users, so long as those parties agree to keep this information confidential.'
        },
        rights: {
          title: 'Your Rights',
          content: 'Under applicable data protection laws, you have certain rights regarding your personal information:',
          items: [
            'Right to access your personal data',
            'Right to rectification of inaccurate data',
            'Right to erasure of your data',
            'Right to restrict processing',
            'Right to data portability',
            'Right to object to processing'
          ]
        },
        contact: {
          title: 'Contact Us',
          content: 'If you have any questions about this Privacy Policy or our data practices, please contact us at:'
        }
      },
      printButton: 'Print',
      closeButton: 'Close'
    },

    // Impressum
    impressum: {
      contact: 'Contact',
      registration: 'Business Registration',
      responsible: 'Responsible for Content',
      disputeResolution: 'Online Dispute Resolution',
      disputeText: 'The European Commission provides a platform for online dispute resolution (OS):',
      disputeDisclaimer: 'We are not obligated and not willing to participate in dispute resolution proceedings before a consumer arbitration board.',
      closeButton: 'Close'
    }
  },

  de: {
    // Header
    nav: {
      aboutUs: 'Über Uns',
      gallery: 'Galerie',
      reservations: 'Reservierungen',
      contact: 'Kontakt'
    },

    // Company Info
    company: {
      name: 'SLÁI Events',
      tagline: 'Premium Food Truck Catering',
      description: 'Von unserem mobilen Food Truck bis zu Ihrer besonderen Gelegenheit bringen wir die besten Craft-Biere und Gourmet Würstchen direkt zu Ihnen. Ob Gartenparty, Firmenveranstaltung oder Cocktailfeier – SLÁI Events liefert außergewöhnlichen Geschmack und unvergessliche Erlebnisse.',
      email: 'info@slaievents.com',
      website: 'slaievents.com',
      addressLine1: 'Bärensteinstraße 2',
      addressLine2: '12685-Berlin',
      hours: 'Mo–So: Nach Vereinbarung'
    },

    // About Us Page
    aboutUs: {
      stats: {
        clientsLabel: 'Zufriedene Kunden',
        eventsLabel: 'Veranstaltungen Betreut',
        ratingLabel: 'Kundenbewertung'
      },
      bookNow: 'Jetzt Buchen',
      servicesTitle: 'Unsere Dienstleistungen',
      servicesSubtitle: 'Von Streetfood bis zu anspruchsvollen Events – wir haben alles im Griff',
      services: [
        {
          title: 'Food Truck Service',
          description: 'Unser mobiler Food Truck bringt die Party zu Ihnen! Perfekt für Straßenveranstaltungen, Festivals und lockere Zusammenkünfte.',
          features: ['Premium Craft-Biere', 'Gourmet Würstchen', 'Schneller Service', 'Perfekt für Events']
        },
        {
          title: 'Gartenpartys',
          description: 'Verwandeln Sie Ihren Garten in ein kulinarisches Paradies. Wir catern intime Gartenpartys mit Stil und Geschmack.',
          features: ['Individuelle Menüplanung', 'Kompletter Auf- und Abbau', 'Getränke-Pairing', 'Outdoor-Grillen']
        },
        {
          title: 'Cocktail-Events',
          description: 'Verfeinern Sie Ihre Cocktailparty mit unseren Signatur Cocktails und Gourmet-Wurstauswahl.',
          features: ['Signatur-Cocktails', 'Vorspeisenplatten', 'Professioneller Service', 'Elegante Präsentation']
        },
        {
          title: 'Geburtstagsfeiern',
          description: 'Machen Sie Ihren besonderen Tag unvergesslich mit unseren festlichen Geburtstags-Catering-Paketen für alle Altersgruppen.',
          features: ['Geburtstagspakete', 'Thematische Dekoration', 'Individuelle Tortenoptionen', 'Unterhaltungskoordination']
        },
        {
          title: 'Firmenevents',
          description: 'Beeindrucken Sie Ihre Kollegen und Kunden mit unseren professionellen Firmen-Catering-Services und Teambuilding-Erlebnissen.',
          features: ['Professioneller Aufbau', 'Angepasste Menüs', 'Teambuilding-Aktivitäten', 'Komplettes Event-Management']
        },
        {
          title: 'Ihr Traumevent',
          description: 'Haben Sie eine einzigartige Idee? Wir verwirklichen Ihre Vision mit maßgeschneidertem Catering für jeden Anlass.',
          features: ['Personalisierte Menüerstellung', 'Flexible Zeiten & Aufbau', 'Individuelle Themen möglich', 'Persönliche Eventplanung']
        }
      ],
      storyTitle: 'Unsere Geschichte',
      storyParagraphs: [
        'SLÁI Events begann mit einer einfachen Leidenschaft: Menschen durch großartiges Essen und Getränke zusammenzubringen. Was als kleiner Food Truck begann, ist zu einem Full-Service-Catering-Unternehmen gewachsen, das sich auf unvergessliche Events spezialisiert hat.',
        'Wir sind stolz auf unsere Auswahl an Craft-Bieren und Gourmet Würstchen, sorgfältig kuratiert, um die perfekte Kombination für jeden Anlass zu bieten. Ob lockere Gartenparty oder elegantes Cocktail-Event – wir bringen die gleiche Hingabe und Qualität zu jedem Treffen.',
        'Unser Team ist leidenschaftlich daran interessiert, unvergessliche Erlebnisse zu schaffen. Wir servieren nicht nur Essen und Getränke – wir schaffen Momente, die Menschen zusammenbringen und bleibende Eindrücke hinterlassen.'
      ]
    },

    // Gallery Page
    gallery: {
      title: 'Galerie',
      subtitle: 'Entdecken Sie unsere köstlichen Angebote und unvergesslichen Events',
      categories: {
        all: 'Alle',
        foodTruck: 'Food Truck',
        food: 'Essen',
        drinks: 'Getränke',
        events: 'Veranstaltungen'
      },
      ctaTitle: 'Möchten Sie in unserer Galerie vorgestellt werden?',
      ctaSubtitle: 'Buchen Sie ein Event bei uns und schaffen Sie Ihre eigenen unvergesslichen Erinnerungen!',
      ctaButton: 'Event Buchen',
      imageAlts: [
        'SLÁI Events Food Truck bei Outdoor-Event',
        'Gourmet-Würstchen mit Craft-Bier',
        'Gartenparty-Aufbau',
        'Handwerkliche Wurstauswahl',
        'Craft-Bier-Auswahl',
        'Cocktailparty-Catering',
        'Food Truck Innenraum',
        'Glückliche Kunden bei Event',
        'Bier-Cocktails'
      ]
    },

    // Reservations Page
    reservations: {
      title: 'Reservierungen',
      subtitle: 'Wählen Sie Ihren Event-Typ und lassen Sie uns gemeinsam etwas Unvergessliches schaffen',
      eventTypes: [
        {
          name: 'Food Truck Buchung',
          description: 'Buchen Sie unseren Food Truck für Ihr Event',
          minGuests: 20,
          duration: '2-4 Stunden'
        },
        {
          name: 'Gartenparty Catering',
          description: 'Full-Service-Catering für Gartenpartys',
          minGuests: 20,
          duration: '3-6 Stunden'
        },
        {
          name: 'Cocktailparty',
          description: 'Premium-Cocktail- und Vorspeisenservice',
          minGuests: 15,
          duration: '2-5 Stunden'
        },
        {
          name: 'Geburtstagsfeier',
          description: 'Festliches Geburtstags-Catering',
          minGuests: 20,
          duration: '3-5 Stunden'
        },
        {
          name: 'Firmenevent',
          description: 'Professionelles Catering für Geschäftsveranstaltungen',
          minGuests: 25,
          duration: '3-8 Stunden'
        },
        {
          name: 'Ihr Traumevent',
          description: 'Was stellen Sie sich vor? Lassen Sie uns gemeinsam etwas Einzigartiges kreieren!',
          minGuests: 20,
          duration: 'Flexibel',
          isSpecial: true
        }
      ],
      minGuests: 'Mind.',
      guests: 'Gäste',
      selectEvent: 'Event Auswählen',
      howItWorksTitle: 'So Funktioniert Es',
      steps: [
        {
          title: 'Event-Typ Wählen',
          description: 'Wählen Sie den perfekten Service für Ihren Anlass'
        },
        {
          title: 'Details Ausfüllen',
          description: 'Geben Sie Ihre Event-Informationen und Präferenzen an'
        },
        {
          title: 'Wir Kontaktieren Sie',
          description: 'Unser Team wird die Details bestätigen und finalisieren'
        }
      ],
      formTitle: 'Buchen',
      formDescription: 'Füllen Sie Ihre Daten aus und wir melden uns in Kürze, um Ihre Reservierung zu bestätigen.',
      formLabels: {
        fullName: 'Vollständiger Name',
        email: 'E-Mail',
        phone: 'Telefonnummer',
        preferredDate: 'Bevorzugtes Datum',
        preferredTime: 'Bevorzugte Uhrzeit',
        numberOfGuests: 'Anzahl der Gäste',
        specialRequests: 'Besondere Wünsche oder Fragen',
        minimum: 'Mindestens'
      },
      formPlaceholders: {
        fullName: 'Max Mustermann',
        email: 'max@beispiel.de',
        phone: '+49 (123) 456-7890',
        specialRequests: 'Diätetische Einschränkungen, besondere Anforderungen oder Fragen...'
      },
      formButtons: {
        cancel: 'Abbrechen',
        submit: 'Anfrage Senden'
      },
      successTitle: 'Reservierungsanfrage Erhalten!',
      successDescription: 'Wir werden Sie in Kürze kontaktieren, um Ihre Buchung zu bestätigen.'
    },

    // Contact Page
    contact: {
      title: 'Kontakt',
      subtitle: 'Haben Sie Fragen? Wir freuen uns von Ihnen zu hören. Senden Sie uns eine Nachricht und wir antworten so schnell wie möglich.',
      infoCards: {
        email: 'E-Mail',
        location: 'Standort',
        hours: 'Öffnungszeiten'
      },
      formTitle: 'Senden Sie Uns Eine Nachricht',
      formSubtitle: 'Füllen Sie das Formular aus und unser Team meldet sich innerhalb von 24 Stunden bei Ihnen',
      formLabels: {
        name: 'Ihr Name',
        email: 'Ihre E-Mail',
        subject: 'Betreff',
        message: 'Ihre Nachricht'
      },
      formPlaceholders: {
        name: 'Max Mustermann',
        email: 'max@beispiel.de',
        subject: 'Wie können wir Ihnen helfen?',
        message: 'Erzählen Sie uns mehr über Ihre Anfrage...'
      },
      sendButton: 'Nachricht Senden',
      successTitle: 'Nachricht Gesendet!',
      successDescription: 'Vielen Dank für Ihre Kontaktaufnahme. Wir melden uns in Kürze bei Ihnen.',
      mapTitle: 'Finden Sie Uns',
      mapSubtitle: 'Wir Sind Mobil!',
      mapDescription: 'Wir Sind Mobil! - Als Food-Truck-Service kommen wir zu Ihnen! Wir bedienen die gesamte Metropolregion'
    },

    // Footer
    footer: {
      contactTitle: 'Kontakt',
      hoursTitle: 'Öffnungszeiten',
      availability: 'Verfügbar für Events und besondere Anlässe.',
      bookAdvance: 'Buchen Sie im Voraus!',
      copyright: 'Alle Rechte vorbehalten.',
      privacyPolicy: 'Datenschutz'
    },

    // Privacy Policy
    privacy: {
      title: 'Datenschutzerklärung',
      lastUpdated: 'Zuletzt aktualisiert',
      sections: {
        intro: {
          title: 'Einleitung',
          content: 'SLÁI Events ("wir", "unser" oder "uns") verpflichtet sich zum Schutz Ihrer Privatsphäre. Diese Datenschutzerklärung erklärt, wie wir Ihre Informationen sammeln, verwenden, offenlegen und schützen, wenn Sie unsere Website besuchen oder unsere Dienste nutzen.'
        },
        dataCollection: {
          title: 'Informationen, die wir sammeln',
          content: 'Wir sammeln Informationen, die Sie uns direkt zur Verfügung stellen, einschließlich:',
          items: [
            'Name und Kontaktinformationen (E-Mail-Adresse, Telefonnummer)',
            'Veranstaltungsdetails und Präferenzen',
            'Reservierungsinformationen (Datum, Uhrzeit, Anzahl der Gäste)',
            'Kommunikationsaufzeichnungen und Feedback',
            'Alle anderen Informationen, die Sie uns zur Verfügung stellen'
          ]
        },
        dataUsage: {
          title: 'Wie wir Ihre Informationen verwenden',
          content: 'Wir verwenden die gesammelten Informationen für verschiedene Zwecke:',
          items: [
            'Zur Bearbeitung und Verwaltung Ihrer Reservierungen und Anfragen',
            'Um mit Ihnen über unsere Dienstleistungen zu kommunizieren',
            'Um Ihnen Updates und Werbematerialien zu senden (mit Ihrer Zustimmung)',
            'Zur Verbesserung unserer Website und Dienste',
            'Zur Erfüllung gesetzlicher Verpflichtungen'
          ]
        },
        dataProtection: {
          title: 'Datenschutz',
          content: 'Wir implementieren angemessene technische und organisatorische Sicherheitsmaßnahmen, um Ihre persönlichen Daten vor unbefugtem Zugriff, Änderung, Offenlegung oder Zerstörung zu schützen. Keine Übertragungsmethode über das Internet ist jedoch 100% sicher, und wir können keine absolute Sicherheit garantieren.'
        },
        cookies: {
          title: 'Cookies',
          content: 'Unsere Website kann Cookies und ähnliche Tracking-Technologien verwenden, um Ihr Browsererlebnis zu verbessern. Sie können Ihren Browser so einstellen, dass alle oder einige Browser-Cookies abgelehnt werden oder Sie benachrichtigt werden, wenn Websites Cookies setzen oder darauf zugreifen.'
        },
        thirdParties: {
          title: 'Weitergabe an Dritte',
          content: 'Wir verkaufen, tauschen oder übertragen Ihre personenbezogenen Daten nicht an Dritte, es sei denn, wir informieren die Nutzer vorab. Dies gilt nicht für Website-Hosting-Partner und andere Parteien, die uns beim Betrieb unserer Website, der Führung unseres Geschäfts oder der Bedienung unserer Nutzer unterstützen, solange diese Parteien sich verpflichten, diese Informationen vertraulich zu behandeln.'
        },
        rights: {
          title: 'Ihre Rechte',
          content: 'Nach geltendem Datenschutzrecht haben Sie bestimmte Rechte in Bezug auf Ihre persönlichen Daten:',
          items: [
            'Recht auf Zugang zu Ihren persönlichen Daten',
            'Recht auf Berichtigung unrichtiger Daten',
            'Recht auf Löschung Ihrer Daten',
            'Recht auf Einschränkung der Verarbeitung',
            'Recht auf Datenübertragbarkeit',
            'Widerspruchsrecht gegen die Verarbeitung'
          ]
        },
        contact: {
          title: 'Kontakt',
          content: 'Wenn Sie Fragen zu dieser Datenschutzerklärung oder unseren Datenpraktiken haben, kontaktieren Sie uns bitte unter:'
        }
      },
      printButton: 'Drucken',
      closeButton: 'Schließen'
    },

    // Impressum
    impressum: {
      contact: 'Kontakt',
      registration: 'Gewerbeanmeldung',
      responsible: 'Inhaltlich Verantwortlich',
      disputeResolution: 'Online-Streitbeilegung',
      disputeText: 'Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit:',
      disputeDisclaimer: 'Wir sind nicht verpflichtet und nicht bereit, an einem Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.',
      closeButton: 'Schließen'
    }
  }
};

export const getTranslation = (lang = 'en') => {
  return translations[lang] || translations.en;
};